/**
 * Duration-warning thresholds for live meetings (header elapsed clock).
 *
 * Defaults: first prompt at 90 minutes; each Continue advances by 30 minutes.
 * Override in local `.env` for manual QA without waiting (restart Vite after change):
 *   VITE_DURATION_WARNING_FIRST_SECONDS=15
 *   VITE_DURATION_WARNING_INTERVAL_SECONDS=10
 */

const DEFAULT_FIRST_AT_SECONDS = 90 * 60
const DEFAULT_INTERVAL_SECONDS = 30 * 60

/**
 * Parses a positive integer second count from an env string; falls back when missing/invalid.
 */
export function parseDurationWarningSeconds(
  raw: string | undefined,
  fallback: number
): number {
  if (raw === undefined || raw.trim() === '') return fallback
  const parsed = Number.parseInt(raw, 10)
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback
  return parsed
}

export const DURATION_WARNING_FIRST_AT_SECONDS = parseDurationWarningSeconds(
  import.meta.env.VITE_DURATION_WARNING_FIRST_SECONDS,
  DEFAULT_FIRST_AT_SECONDS
)

export const DURATION_WARNING_INTERVAL_SECONDS = parseDurationWarningSeconds(
  import.meta.env.VITE_DURATION_WARNING_INTERVAL_SECONDS,
  DEFAULT_INTERVAL_SECONDS
)

export type DurationWarningStep = 'closed' | 'checkin' | 'confirm'

/**
 * Next threshold after Continue. Uses max(current, elapsed) so a long pause
 * (wall-clock elapsed jumps on resume) cannot leave next ≤ elapsed and re-fire.
 */
export function advanceDurationWarningAt(
  currentNextAtSeconds: number,
  elapsedSeconds: number
): number {
  return (
    Math.max(currentNextAtSeconds, elapsedSeconds) + DURATION_WARNING_INTERVAL_SECONDS
  )
}

export function shouldTriggerDurationWarning(input: {
  elapsedSeconds: number
  nextWarningAtSeconds: number
  /** Session is actively capturing / streaming (not idle / interrupted-only). */
  isRecording: boolean
  isPaused: boolean
  warningOpen: boolean
  isEnding: boolean
  feedbackOpen: boolean
}): boolean {
  if (input.warningOpen || input.isEnding || input.feedbackOpen) return false
  if (!input.isRecording || input.isPaused) return false
  return input.elapsedSeconds >= input.nextWarningAtSeconds
}
